import { Button } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import { TabLine } from "@dropbox/dig-icons/assets";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { PAP_Click_AddSource } from "@mirage/analytics/events/types/click_add_source";
import { PAP_Click_RemoveSource } from "@mirage/analytics/events/types/click_remove_source";
import {
  getBrowserTabsAttachments,
  useTabInfos,
} from "@mirage/mosaics/Chat/components/chat/BrowserTabs/BrowserTabsIngestion";
import { BrowserTabsPickerMenuItems } from "@mirage/mosaics/Chat/components/chat/BrowserTabs/BrowserTabsPickerMenu";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import {
  BreakpointClass,
  useBreakpointClass,
} from "@mirage/shared/responsive/breakpoints";
import i18n from "@mirage/translations";
import { memo, useCallback, useMemo } from "react";
import styles from "./BrowserTabSources.module.css";

interface BrowserTabSourcesButtonProps {
  sources: ChatSource[];
  onAddSource: (source: ChatSource) => void;
  onRemoveSource: (source: ChatSource) => void;
  logEvent: (
    event: PAPEvent,
    overrides?: {
      actionSurfaceComponent?: ActionSurfaceComponent;
    },
  ) => void;
}
export const BrowserTabSourcesButton = memo(
  ({
    sources,
    onAddSource,
    onRemoveSource,
    logEvent,
  }: BrowserTabSourcesButtonProps) => {
    const { windowTabInfos, sortedTabInfos, refreshTabInfos } = useTabInfos();
    const browserTabSources = useMemo(
      () => sources.filter((source) => source.type === "browser_tab_snapshot"),
      [sources],
    );
    const selectedTabIDs = useMemo(
      () => new Set(browserTabSources.map((source) => source.tabInfo.id)),
      [browserTabSources],
    );
    const handleSetSelectedTabIDs = useCallback(
      async (newSelectedTabIDs: Set<number>) => {
        // check for newly added/removed sources
        const newTabIDs = Array.from(newSelectedTabIDs).filter(
          (tabID) => !selectedTabIDs.has(tabID),
        );
        const removedSources = browserTabSources.filter(
          (source) => !newSelectedTabIDs.has(source.tabInfo.id),
        );

        // ingest snapshot from new tabIDs
        const newSources = await getBrowserTabsAttachments(new Set(newTabIDs));
        newSources.forEach((source) => {
          onAddSource(source);
          logEvent(
            PAP_Click_AddSource({
              actionType: "browser_tab",
            }),
          );
        });
        removedSources.forEach((source) => {
          onRemoveSource(source);
          logEvent(PAP_Click_RemoveSource());
        });
      },
      [
        browserTabSources,
        logEvent,
        onAddSource,
        onRemoveSource,
        selectedTabIDs,
      ],
    );
    const slideSubmenu = useSlideSubmenu();

    if (windowTabInfos.size === 0) {
      return null; // unable to communicate with the extension / waiting for response
    }

    return (
      <Menu.Wrapper
        slideSubmenu={slideSubmenu}
        closeOnSelection={false}
        onToggle={({ isOpen }) => {
          if (isOpen) {
            refreshTabInfos();
          }
        }}
      >
        {({ getContentProps, getTriggerProps }) => (
          <>
            <Button
              {...getTriggerProps()}
              variant="filled"
              size="large"
              className={styles.BrowserTabSourcesButton}
              withDropdownIcon
            >
              <UIIcon src={TabLine} size="standard" />
              <Text
                size="small"
                tagName="span"
                color="standard"
                isBold
                className={styles.BrowserTabSourcesButtonText}
              >
                {i18n.t("compose_assistant_browser_tab_sources_button_label")}
                <span className={styles.BrowserTabSourcesButtonTextCount}>
                  {browserTabSources.length > 0 &&
                    ` ${browserTabSources.length}`}
                </span>
              </Text>
            </Button>
            <Menu.Content
              {...getContentProps()}
              auto
              className={styles.BrowserTabSourcesMenuContent}
            >
              <BrowserTabsPickerMenuItems
                windowTabInfos={windowTabInfos}
                sortedTabInfos={sortedTabInfos}
                selectedTabIDs={selectedTabIDs}
                setSelectedTabIDs={handleSetSelectedTabIDs}
              />
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>
    );
  },
);
BrowserTabSourcesButton.displayName = "BrowserTabSourcesButton";

/**
 * Use slide submenu when window is small.
 */
function useSlideSubmenu() {
  const breakpointClass = useBreakpointClass();
  switch (breakpointClass) {
    case BreakpointClass.XSMALL:
    case BreakpointClass.SMALL:
      return true;
    case BreakpointClass.MEDIUM:
    case BreakpointClass.LARGE:
    case BreakpointClass.XLARGE:
      return false;
    default:
      breakpointClass satisfies never;
      throw new Error(`Unexpected breakpoint class: ${breakpointClass}`);
  }
}
